import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Check, Crown, Zap, ArrowRight, CreditCard, Bug } from 'lucide-react';
import { trackCheckoutStarted } from '@/utils/amplitudeEvents';
import { useCompany } from '@/contexts/CompanyContext';
import { useToast } from '@/hooks/use-toast';
import { PricingPageSEO } from '@/components/seo/SEOHead';

const Pricing = () => {
  const {
    createCheckoutSession,
    openCustomerPortal,
    loading,
    subscriptionTier,
    subscribed,
    subscriptionEnd,
    testEdgeFunction
  } = useSubscription();
  const {
    user,
    isAdmin
  } = useAuth();
  const navigate = useNavigate();
  const {
    toast
  } = useToast();
  const { currentCompany } = useCompany();
  const plans = [{
    name: 'Pro',
    price: 199,
    priceId: window.location.hostname === 'zentrixinsights.com'
      ? 'price_1SX2rs68C1gbVF0CYmqIvsbS'        // Production (current brand)
      : 'price_1SX2tA68C1gbVF0CRxokNcch',       // Sandbox (Zentrix Insights)
    description: '7-day free trial • No credit card required',
    icon: Crown,
    features: ['Unlimited assessments', 'Advanced analytics & insights', 'Priority support', 'Team comparison tools', 'Custom branding', 'Detailed development plans', 'Export to PDF', 'Advanced reporting'],
    popular: true,
    isFree: false
  }];
  const handleSelectPlan = async (plan: typeof plans[0]) => {
    // For Pro plan, check if user is authenticated
    if (!user) {
      // Store the plan information in sessionStorage before redirecting to signup
      const planInfo = {
        name: plan.name,
        priceId: plan.priceId,
        planName: plan.name
      };
      sessionStorage.setItem('selectedPlan', JSON.stringify(planInfo));
      console.log('Pricing: Storing plan info and redirecting to signup', planInfo);
      toast({
        title: "Sign up required",
        description: "Please create an account to continue with your Pro subscription."
      });
      navigate('/login?tab=signup&from=pricing');
      return;
    }

    // User is authenticated, proceed directly with checkout
    console.log('Pricing: User authenticated, proceeding with checkout for:', plan.name);
    try {
      const trialDaysRemaining = subscriptionTier === 'trial' && subscriptionEnd
        ? Math.max(0, Math.ceil((new Date(subscriptionEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
        : null;
      trackCheckoutStarted({
        company_id: currentCompany?.company_id || null,
        company_name: currentCompany?.company_name || null,
        user_id: user?.id || null,
        user_email: user?.email || null,
        user_name: user?.user_metadata?.full_name || null,
        current_plan: subscriptionTier,
        target_plan: plan.name,
        trial_days_remaining: trialDaysRemaining,
        source: 'pricing_page',
      });
      await createCheckoutSession('auto', plan.name);
    } catch (error) {
      console.error('Pricing: Error starting checkout:', error);
      toast({
        title: "Checkout Error",
        description: "Failed to start checkout. Please try again or contact support.",
        variant: "destructive"
      });
    }
  };
  const handleManageSubscription = async () => {
    try {
      await openCustomerPortal();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to open customer portal. Please try again.",
        variant: "destructive"
      });
    }
  };
  const handleTestConnection = async () => {
    console.log('Pricing: Testing backend connection...');
    await testEdgeFunction();
  };
  const isCurrentPlan = (planName: string) => {
    if (!user) return false;
    return subscriptionTier === planName;
  };
  const getButtonText = (plan: typeof plans[0]) => {
    if (loading) {
      return <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-3"></div>
          Processing...
        </div>;
    }
    if (!user) {
      return 'Start 7-Day Free Trial';
    }
    if (isCurrentPlan(plan.name)) {
      return 'Current Plan';
    }
    return `Upgrade to ${plan.name}`;
  };
  return (
    <>
      <PricingPageSEO />
      <div className="min-h-screen bg-[var(--page-bg)]">
        <div className="tesla-container tesla-section">
          {/* Header */}
          <div className="text-center mb-20 animate-fade-in-up">
          <h1 className="text-[24px] font-semibold text-[var(--text-primary)] mb-6 tracking-normal">
            Choose Your Plan
          </h1>
          <p className="text-[16px] text-[var(--text-secondary)] max-w-2xl mx-auto font-medium">
            Start free and upgrade when you're ready for advanced features.
          </p>

          {/* Current Subscription Status */}
          {user && subscribed && <div className="mt-8">
              <Badge className="tesla-accent-bg text-white px-6 py-2 text-[13px]">
                Current Plan: {subscriptionTier || 'Pro'}
              </Badge>
            </div>}

          {/* Debug Panel for Development */}
          {user && <div className="mt-4">

            </div>}
        </div>

        {/* Pricing Card */}
        <div className="max-w-md mx-auto mb-20">
          {plans.map(plan => {
          const Icon = plan.icon;
          const isCurrent = isCurrentPlan(plan.name);
          return <Card key={plan.name} className={`tesla-card relative animate-scale-in transition-all duration-150 ${plan.popular ? 'ring-2 ring-[var(--accent-hex)]' : ''} ${isCurrent ? 'border-status-success border-2' : ''}`}>
                {plan.popular && <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className="tesla-accent-bg px-4 py-1 text-white font-semibold tracking-normal">
                      Most Popular
                    </Badge>
                  </div>}

                {isCurrent && <div className="absolute -top-3 right-4">
                    <Badge className="bg-status-success px-4 py-1 text-white font-semibold">
                      Current
                    </Badge>
                  </div>}

                <CardHeader className="text-center pb-6">
                  <div className="flex justify-center mb-6">
                    <div className={`p-4 rounded-sm ${plan.popular ? 'bg-[var(--accent-hex)]/10' : 'bg-[var(--surface-raised-hex)]'}`}>
                      <Icon className={`h-8 w-8 ${plan.popular ? 'tesla-accent' : 'text-[var(--text-secondary)]'}`} />
                    </div>
                  </div>

                  <CardTitle className="text-[24px] font-semibold text-[var(--text-primary)] mb-3 tracking-normal">
                    {plan.name}
                  </CardTitle>
                  <CardDescription className="text-[var(--text-secondary)] text-[16px]">
                    {plan.description}
                  </CardDescription>

                  <div className="mt-6">
                    <span className="text-[24px] font-semibold text-[var(--text-primary)] tracking-normal">
                      ${plan.price}
                    </span>
                    <span className="text-[var(--text-secondary)] text-[16px] font-medium">/month</span>
                  </div>
                </CardHeader>

                <CardContent className="pt-0">
                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, index) => <li key={index} className="flex items-center">
                        <Check className="h-5 w-5 text-status-success mr-4 flex-shrink-0" />
                        <span className="text-[var(--text-secondary)] font-medium">{feature}</span>
                      </li>)}
                  </ul>

                  <Button onClick={() => handleSelectPlan(plan)} disabled={loading || user && isCurrent} className={`w-full h-14 text-[13px] font-semibold tracking-normal ${plan.popular ? 'tesla-accent-bg hover:opacity-90' : 'tesla-button'} ${user && isCurrent ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    {getButtonText(plan)}
                    {!isCurrent && !loading && <ArrowRight className="ml-2 h-4 w-4" />}
                  </Button>
                </CardContent>
              </Card>;
        })}
        </div>

        {/* Subscription Management */}
        {user && subscribed && <div className="text-center mb-20">
            <Card className="tesla-card max-w-md mx-auto p-6">
              <h3 className="text-[16px] font-semibold text-[var(--text-primary)] mb-4">Manage Your Subscription</h3>
              <p className="text-[var(--text-secondary)] mb-6">
                Update payment methods, view billing history, or cancel your subscription.
              </p>
              <Button onClick={handleManageSubscription} variant="outline" className="w-full h-12">
                <CreditCard className="mr-2 h-4 w-4" />
                Manage Billing
              </Button>
            </Card>
          </div>}

        {/* FAQ Section */}
        <div className="mb-20 animate-slide-in">
          <h2 className="text-[20px] font-semibold text-[var(--text-primary)] mb-12 text-center tracking-normal">
            Frequently Asked Questions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="tesla-card p-8">
              <h3 className="text-[16px] font-semibold text-[var(--text-primary)] mb-4 tracking-normal">
                Can I upgrade anytime?
              </h3>
              <p className="text-[var(--text-secondary)] font-medium leading-relaxed">
                Yes, you can upgrade from Free to Pro at any time. Changes take effect immediately.
              </p>
            </div>
            <div className="tesla-card p-8">
              <h3 className="text-[16px] font-semibold text-[var(--text-primary)] mb-4 tracking-normal">
                Is there a free trial for Pro?
              </h3>
              <p className="text-[var(--text-secondary)] font-medium leading-relaxed">
                The Free plan lets you try our core features. Upgrade to Pro when you need advanced analytics and unlimited assessments.
              </p>
            </div>
            <div className="tesla-card p-8">
              <h3 className="text-[16px] font-semibold text-[var(--text-primary)] mb-4 tracking-normal">
                What payment methods do you accept?
              </h3>
              <p className="text-[var(--text-secondary)] font-medium leading-relaxed">
                We accept all major credit cards and PayPal through our secure Stripe payment system.
              </p>
            </div>
            <div className="tesla-card p-8">
              <h3 className="text-[16px] font-semibold text-[var(--text-primary)] mb-4 tracking-normal">
                Can I cancel anytime?
              </h3>
              <p className="text-[var(--text-secondary)] font-medium leading-relaxed">
                Yes, you can cancel your Pro subscription at any time. You'll continue to have Pro access until your current period ends.
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center animate-fade-in-up">
          <div className="tesla-card max-w-2xl mx-auto p-12">
            <h2 className="text-[20px] font-semibold text-[var(--text-primary)] mb-6 tracking-normal">
              Ready to get started?
            </h2>
            <p className="text-[var(--text-secondary)] text-[16px] mb-8 font-medium leading-relaxed">
              Join thousands of professionals who use our platform to understand personality and build better teams.
            </p>
            <Button onClick={() => {
            if (user) {
              if (isAdmin()) {
                navigate('/admin');
              } else {
                navigate('/dashboard');
              }
            } else {
              navigate('/login?tab=signup');
            }
          }} className="tesla-accent-bg hover:opacity-90 h-14 px-12 text-[13px] font-semibold tracking-normal">
              {user ? 'Go to Dashboard' : 'Get Started Today'}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
        </div>
      </div>
    </>
  );
};

export default Pricing;
